import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Save, Trash2, FileText, Truck, ChevronDown, RefreshCw, X, Download, Plus, Edit2,
  Loader2, AlertCircle, CheckCircle2, Calendar, Tag, MapPin, User, Phone, Mail, CreditCard,
  Package, Paperclip, Upload, Wallet, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import PvServiceModal from "./PvServiceModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUSES = {
  pending:           { label: "În așteptare",                    cls: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  // Garanție
  pickup_issued:     { label: "Preluare Client",                cls: "bg-orange-500/15 text-orange-300 border-orange-500/40" },
  in_transit:        { label: "În drum spre service",           cls: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40" },
  at_service:        { label: "Verificare Service",             cls: "bg-violet-500/15 text-violet-300 border-violet-500/40" },
  delivery_issued:   { label: "În livrare către client",        cls: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
  // Retur
  in_transit_warehouse:        { label: "Către Depozit",                  cls: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40" },
  at_warehouse_pending_refund: { label: "În verificare, Bani de returnat",cls: "bg-violet-500/15 text-violet-300 border-violet-500/40" },
  // Schimb (4-step flow, 2026-08-21)
  outbound_in_delivery: { label: "În livrare către client",    cls: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
  return_in_transit:    { label: "În tranzit către depozit",   cls: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40" },
  // Livrare (3-step flow, 2026-08-21)
  in_delivery:       { label: "În drum spre client",           cls: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
  // Common end
  completed:         { label: "Finalizată",                     cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  // Legacy aliases (cereri vechi din schema veche)
  awb_issued:        { label: "Preluare Client",                cls: "bg-orange-500/15 text-orange-300 border-orange-500/40" },
  verified:          { label: "Verificare Service",             cls: "bg-violet-500/15 text-violet-300 border-violet-500/40" },
  refund_redeliver:  { label: "Bani / AWB Livrare",             cls: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
  refunded:          { label: "Finalizată (Bani Returnați)",    cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  product_returned:  { label: "În tranzit către depozit",       cls: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40" },
  outbound_delivered:{ label: "În tranzit către depozit",       cls: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40" }, // legacy collapsed
};
const TYPES = { garantie: "Garanție", retur: "Retur", schimb: "Schimb", livrare: "Livrare" };

const PLATFORM_LABEL = {
  emag_ro_fbe: "EMAG-RO-FBE", emag_ro_bau: "EMAG-RO-BAU",
  emag_bg_fbe: "EMAG-BG-FBE", emag_bg_bau: "EMAG-BG-BAU",
  emag_hu_fbe: "EMAG-HU-FBE", emag_hu_bau: "EMAG-HU-BAU",
};

const fmtDate = (s) => s ? new Date(s).toLocaleDateString("ro-RO", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const fmtDateTime = (s) => s ? new Date(s).toLocaleString("ro-RO", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

export default function ReturnDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [addAwbOpen, setAddAwbOpen] = useState(false);

  // Drafts for inline edits
  const [draft, setDraft] = useState({});

  // PV Service modal state.
  //   mode = "pv_only"          → legacy: just generate the PV PDF + auto-print
  //   mode = "pv_and_deliver"   → PV PDF + AWB Livrare + email-with-PV all-in-one
  const [pvOpen, setPvOpen] = useState(false);
  const [pvMode, setPvMode] = useState("pv_only");
  // AWB popup shown right after the combined flow finishes — gives the
  // operator a Deschide / Descarcă button for the freshly-issued AWB.
  const [awbPopup, setAwbPopup] = useState(null); // { number, label }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/returns/${id}`);
        if (!cancelled) {
          setData(r.data);
          setDraft({});
        }
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const setDraftField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const persist = async (patch) => {
    setSaving(true); setError(""); setNotice("");
    try {
      const r = await axios.put(`${API}/returns/${id}`, patch);
      setData(r.data);
      setDraft({});
      setNotice("Salvat.");
      setTimeout(() => setNotice(""), 2000);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  const saveAll = async () => {
    if (Object.keys(draft).length === 0) {
      setNotice("Nimic de salvat.");
      setTimeout(() => setNotice(""), 1500);
      return;
    }
    await persist(draft);
  };

  const handleDelete = async () => {
    if (!window.confirm("Ștergi această cerere și toate atașamentele?")) return;
    try {
      await axios.delete(`${API}/returns/${id}`);
      navigate("/returns");
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };

  const refreshAwb = async (awbNumber) => {
    setError("");
    try {
      const r = await axios.post(`${API}/returns/${id}/awbs/${awbNumber}/refresh`);
      setData(r.data);
      setNotice("Status sincronizat.");
      setTimeout(() => setNotice(""), 1500);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };

  const cancelAwb = async (awbNumber) => {
    if (!window.confirm(`Anulezi AWB ${awbNumber} în Sameday?\n\nAcțiunea încearcă să anuleze AWB-ul direct în Sameday și apoi îl elimină din cerere.`)) return;
    try {
      await axios.delete(`${API}/returns/${id}/awbs/${awbNumber}`);
      const r = await axios.get(`${API}/returns/${id}`);
      setData(r.data);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };

  const removeAwbOnly = async (awbNumber) => {
    if (!window.confirm(`Elimini AWB ${awbNumber} doar din cerere?\n\nAWB-ul NU va fi anulat în Sameday — doar va dispărea din această cerere. Folosește dacă AWB-ul e deja anulat / expirat / introdus din greșeală.`)) return;
    try {
      await axios.delete(`${API}/returns/${id}/awbs/${awbNumber}`, { params: { only_remove: true } });
      const r = await axios.get(`${API}/returns/${id}`);
      setData(r.data);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };

  const addAwb = async (kind) => {
    setAddAwbOpen(false);
    setError("");
    // For warranty deliveries we want a guided "PV Service → AWB Livrare →
    // email with PV attached → AWB popup" sequence instead of creating the
    // AWB on its own. The combined flow is implemented on the backend at
    // `POST /api/returns/{id}/pv-and-deliver`; opening the PV modal in
    // `pv_and_deliver` mode triggers it after the user fills the form.
    const reqType = (current("type") || data?.type || "garantie");
    if (kind === "delivery" && reqType === "garantie") {
      setPvMode("pv_and_deliver");
      setPvOpen(true);
      return;
    }
    // For livrare requests, ask if the operator wants Ramburs (COD) on the AWB.
    // Only prompts when the kind is delivery/pickup — schimb still uses the
    // dual-AWB path without COD.
    let payload = { kind };
    if (reqType === "livrare" && kind === "delivery") {
      const raw = window.prompt("Ramburs (COD) pe AWB? Introdu suma în RON sau lasă gol pentru 0.\n(ex: 199.90)", "");
      if (raw !== null) {
        const parsed = parseFloat(String(raw).replace(",", ".").trim());
        if (!isNaN(parsed) && parsed > 0) payload.cash_on_delivery = parsed;
      }
    }
    const label = kind === "pickup" ? "ridicare" : kind === "delivery" ? "livrare" : "schimb (2 AWB-uri)";
    setNotice(`Se generează AWB ${label}...`);
    try {
      // AWB creation calls Sameday synchronously and may take 10-15s
      // (create_awb round-trip + status transition). Override the default
      // 25s axios timeout so slow Sameday responses don't surface as a
      // generic "Network Error" on production.
      await axios.post(`${API}/returns/${id}/awbs`, payload, { timeout: 60000 });
      const r = await axios.get(`${API}/returns/${id}`);
      setData(r.data);
      setNotice(kind === "exchange" ? "2 AWB-uri generate." : payload.cash_on_delivery ? `AWB generat cu ramburs ${payload.cash_on_delivery} RON.` : "AWB generat.");
      setTimeout(() => setNotice(""), 3500);
    } catch (e) { setError(e.response?.data?.detail || e.message); setNotice(""); }
  };

  const handlePvAwbCreated = (res) => {
    // Combined flow returned successfully — refresh + show the AWB popup
    setAwbPopup({
      number: res?.awb?.number,
      label: "AWB Livrare",
    });
    setNotice("PV generat și AWB Livrare emis. Email trimis cu PV atașat.");
    setTimeout(() => setNotice(""), 3500);
  };

  const openPv = () => { setPvMode("pv_only"); setPvOpen(true); };

  const handlePvGenerated = async () => {
    try {
      const r = await axios.get(`${API}/returns/${id}`);
      setData(r.data);
    } catch { /* ignore */ }
  };

  if (loading) return <div className="flex h-screen items-center justify-center text-zinc-500"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!data) return <div className="flex h-screen items-center justify-center text-zinc-500">{error || "Cerere inexistentă"}</div>;

  const current = (k) => (draft[k] !== undefined ? draft[k] : data[k]);
  const isDirty = Object.keys(draft).length > 0;

  return (
    <div className="px-4 py-4 sm:px-6 lg:px-8" data-testid="return-detail-page">
      {/* Top action bar */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={() => navigate("/returns")} className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:text-zinc-100">
            <ArrowLeft className="h-3.5 w-3.5" /> Înapoi
          </button>
          {data.request_number != null && (
            <div className="inline-flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5">
              <span className="text-[10px] uppercase tracking-wider text-emerald-400/80">Cerere nr.</span>
              <span data-testid="request-number" className="font-mono text-sm font-bold text-emerald-200">#{String(data.request_number).padStart(4, "0")}</span>
            </div>
          )}
          {/* Type badge — always shown */}
          {(current("type") || data.type) && (
            <div className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-1.5">
              <span className="text-[10px] uppercase tracking-wider text-zinc-500">Tip</span>
              <span className="text-sm font-semibold text-zinc-100">{TYPES[current("type") || data.type] || (current("type") || data.type)}</span>
            </div>
          )}
          {/* Status badge — prominent so user can track flow at a glance for schimb/livrare */}
          {(current("status") || data.status) && (
            <div className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 ${STATUSES[current("status") || data.status]?.cls || "border-zinc-700 bg-zinc-900 text-zinc-100"}`}>
              <span className="text-[10px] uppercase tracking-wider opacity-70">Status</span>
              <span data-testid="top-status-badge" className="text-sm font-semibold">{STATUSES[current("status") || data.status]?.label || (current("status") || data.status)}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <AwbDropdown
            open={addAwbOpen}
            setOpen={setAddAwbOpen}
            onPick={addAwb}
            type={current("type") || data.type}
            awbs={data.awbs || []}
            status={data.status}
          />
          {((current("type") || data.type) === "retur") && (current("status") || data.status) !== "completed" && (current("status") || data.status) !== "refunded" && (
            <button data-testid="action-mark-refunded"
              onClick={async () => {
                if (!window.confirm('Marchezi cererea ca BANI RETURNAȚI (Finalizată)? Clientul primește emailul configurat pentru pasul „completed".')) return;
                try {
                  await axios.post(`${API}/returns/${id}/mark-refunded`);
                  toast.success("Cerere marcată ca Finalizată. Email trimis.");
                  const r = await axios.get(`${API}/returns/${id}`);
                  setData(r.data);
                } catch (e) {
                  toast.error(e.response?.data?.detail || e.message);
                }
              }}
              className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-300 hover:bg-amber-500/20">
              <Wallet className="h-3.5 w-3.5" /> Bani Returnați
            </button>
          )}
          <button data-testid="action-save" onClick={saveAll} disabled={saving || !isDirty}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition ${isDirty ? "bg-emerald-500 text-zinc-950 hover:bg-emerald-400" : "border border-emerald-500/30 bg-emerald-500/10 text-emerald-300"}`}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Salvează
          </button>
          <button data-testid="action-pv" onClick={openPv}
            className="inline-flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:border-orange-500/40 hover:text-orange-300">
            <FileText className="h-3.5 w-3.5" /> PV Service
          </button>
          <button data-testid="action-delete" onClick={handleDelete}
            className="inline-flex items-center gap-1.5 rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-1.5 text-xs text-rose-300 hover:bg-rose-500/20">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {error && (
        <div data-testid="awb-error-banner" className="mb-3 flex items-start gap-2 rounded border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5" /> <span className="flex-1">{error}</span>
          <button onClick={() => setError("")}><X className="h-3.5 w-3.5" /></button>
        </div>
      )}
      {notice && (
        <div className="mb-3 inline-flex items-center gap-2 rounded border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs text-emerald-200">
          <CheckCircle2 className="h-3.5 w-3.5" /> {notice}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[300px_1fr]">
        {/* LEFT */}
        <div className="space-y-4">
          <Card title="Date" icon={Calendar}>
            <KV label="Status">
              <select data-testid="d-status" value={current("status") || "pending"}
                onChange={(e) => setDraftField("status", e.target.value)}
                className={`rounded-md border px-2 py-0.5 text-[10px] font-medium ${STATUSES[current("status")]?.cls || "border-zinc-700 bg-zinc-900 text-zinc-200"}`}>
                {Object.entries(STATUSES).map(([k, c]) => (
                  <option key={k} value={k} className="bg-zinc-900 text-zinc-100">{c.label}</option>
                ))}
              </select>
            </KV>
            <KV label="Tip cerere">
              <select data-testid="d-type" value={current("type") || "garantie"}
                onChange={(e) => setDraftField("type", e.target.value)}
                className="rounded-md border border-zinc-700 bg-zinc-900 px-2 py-0.5 text-[10px] text-zinc-200">
                {Object.entries(TYPES).map(([k, l]) => (
                  <option key={k} value={k} className="bg-zinc-900 text-zinc-100">{l}</option>
                ))}
              </select>
            </KV>
            <KV label="Data cerere">{fmtDate(data.date)}</KV>
            <KV label="Creat">{fmtDateTime(data.created_at)}</KV>
            <KV label="Ultim update">{fmtDateTime(data.updated_at)}</KV>
          </Card>

          <Card title="Notițe interne" icon={Edit2}>
            <InlineTextarea testId="d-notes" value={current("notes")} onChange={(v) => setDraftField("notes", v)} placeholder="Click pentru a adăuga o notiță..." />
          </Card>

          <Card title="Client" icon={User}>
            <KV label="Nume">
              <InlineInput testId="d-cname" value={current("customer_name")} onChange={(v) => setDraftField("customer_name", v)} />
            </KV>
            <KV label="Telefon">
              <InlineInput testId="d-cphone" value={current("customer_phone")} onChange={(v) => setDraftField("customer_phone", v)} />
            </KV>
            <KV label="Email">
              <InlineInput testId="d-cemail" value={current("customer_email")} onChange={(v) => setDraftField("customer_email", v)} placeholder="—" />
            </KV>
            <AddressEditor
              address={current("customer_address")}
              city={current("customer_city")}
              county={current("customer_county")}
              postalCode={current("customer_postal_code")}
              onSave={(patch) => {
                setDraftField("customer_address", patch.address);
                setDraftField("customer_city", patch.city);
                setDraftField("customer_county", patch.county);
                setDraftField("customer_postal_code", patch.postal_code);
                // pickup address mirrors delivery — same field
                setDraftField("pickup_address", patch.address);
                setDraftField("pickup_city", patch.city);
                setDraftField("pickup_county", patch.county);
                setDraftField("pickup_postal_code", patch.postal_code);
              }}
              onPersist={async (patch) => {
                // Persist immediately to DB so the AWB endpoint sees the new address.
                const r = await axios.put(`${API}/returns/${id}`, patch);
                setData(r.data);
                setDraft({});
              }}
            />
            <KV label="IBAN">
              <InlineInput testId="d-iban" value={current("customer_iban")} onChange={(v) => setDraftField("customer_iban", (v || "").toUpperCase())} placeholder="—" />
            </KV>
          </Card>
        </div>

        {/* RIGHT */}
        <div className="space-y-4">
          <Card title="Comandă" icon={Tag}>
            <KVRow label="Nr. comandă">
              {data.order_id ? (
                <button
                  data-testid="open-order-link"
                  onClick={() => navigate(`/orders?order_id=${encodeURIComponent(data.order_id)}&days=365`)}
                  title={`Deschide comanda #${data.order_id} în pagina Comenzi`}
                  className="group inline-flex items-center gap-1 rounded-md border border-transparent px-1.5 py-0.5 font-mono text-sm text-emerald-300 transition hover:border-emerald-500/40 hover:bg-emerald-500/10 hover:text-emerald-200"
                >
                  #{data.order_id}
                  <ExternalLink className="h-3 w-3 opacity-60 transition group-hover:opacity-100" />
                </button>
              ) : (
                <span className="font-mono text-sm text-zinc-500">—</span>
              )}
            </KVRow>
            <KVRow label="Platformă">
              {data.platform && <span className="inline-flex rounded bg-orange-500/10 px-2 py-0.5 font-mono text-[10px] text-orange-300">{PLATFORM_LABEL[data.platform] || data.platform}</span>}
            </KVRow>
            <KVRow label="Motiv">
              <InlineInput testId="d-problem" value={current("problem")} onChange={(v) => setDraftField("problem", v)} placeholder="—" multiline align="right" />
            </KVRow>
            <KVRow label="Dată cerere">{fmtDate(data.date)}</KVRow>
            <KVRow label="Valoare">
              <span className="font-semibold text-zinc-100">{data.value != null ? `${Number(data.value).toFixed(2)} ${data.currency || "RON"}` : "—"}</span>
            </KVRow>
          </Card>

          <ProductsCard products={data.products || []} />

          <AttachmentsCard returnId={id} attachments={data.attachments || []} reload={async () => {
            const r = await axios.get(`${API}/returns/${id}`); setData(r.data);
          }} setError={setError} />

          <AwbsCard awbs={data.awbs || []} onRefresh={refreshAwb} onCancel={cancelAwb} onRemoveOnly={removeAwbOnly} type={current("type") || data.type} returnId={id} />
        </div>
      </div>

      {pvOpen && (
        <PvServiceModal
          returnId={id}
          mode={pvMode}
          onClose={() => setPvOpen(false)}
          onGenerated={handlePvGenerated}
          onAwbCreated={handlePvAwbCreated}
        />
      )}
      {awbPopup && (
        <AwbReadyPopup
          returnId={id}
          awbNumber={awbPopup.number}
          label={awbPopup.label}
          onClose={() => setAwbPopup(null)}
        />
      )}
    </div>
  );
}

// ---------- UI building blocks ----------
function Card({ title, icon: Icon, children }) {
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-950/70">
      <div className="flex items-center gap-2 border-b border-zinc-800 px-4 py-2.5">
        {Icon && <Icon className="h-3.5 w-3.5 text-zinc-500" />}
        <h3 className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">{title}</h3>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function KV({ label, children }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-zinc-900/80 py-2 last:border-b-0 last:pb-0 first:pt-0">
      <span className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</span>
      <div className="text-xs text-zinc-100">{children}</div>
    </div>
  );
}

function KVRow({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-zinc-900/80 py-2 last:border-b-0 last:pb-0 first:pt-0">
      <span className="text-xs text-zinc-400">{label}</span>
      <div className="max-w-[60%] text-right text-xs text-zinc-100">{children}</div>
    </div>
  );
}

function InlineInput({ testId, value, onChange, placeholder, multiline, align }) {
  const [editing, setEditing] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (editing && ref.current) ref.current.focus();
  }, [editing]);

  if (editing) {
    return multiline ? (
      <textarea data-testid={testId} ref={ref} value={value || ""} onChange={(e) => onChange(e.target.value)}
        onBlur={() => setEditing(false)} rows={2}
        className="w-full rounded border border-orange-500/40 bg-zinc-900 px-2 py-1 text-xs text-zinc-100 outline-none" />
    ) : (
      <input data-testid={testId} ref={ref} value={value || ""} onChange={(e) => onChange(e.target.value)}
        onBlur={() => setEditing(false)} onKeyDown={(e) => { if (e.key === "Enter") setEditing(false); }}
        className="w-full rounded border border-orange-500/40 bg-zinc-900 px-2 py-1 text-xs text-zinc-100 outline-none" />
    );
  }
  return (
    <button data-testid={`${testId}-trigger`} onClick={() => setEditing(true)}
      className={`group inline-flex w-full items-center gap-1 rounded px-1 py-0.5 hover:bg-zinc-900 ${align === "right" ? "justify-end text-right" : "text-left"}`}>
      <span className={value ? "text-zinc-100" : "text-zinc-600"}>{value || placeholder || "—"}</span>
      <Edit2 className="h-3 w-3 shrink-0 text-zinc-700 opacity-0 transition group-hover:opacity-100" />
    </button>
  );
}

function InlineTextarea({ testId, value, onChange, placeholder }) {
  return (
    <textarea data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3}
      placeholder={placeholder}
      className="w-full resize-none rounded-md border border-zinc-800 bg-zinc-900/50 px-2 py-1.5 text-xs italic text-zinc-300 outline-none placeholder-zinc-600 focus:border-orange-500/40 focus:not-italic" />
  );
}

// ---------- AWB Dropdown ----------
function AwbDropdown({ open, setOpen, onPick, type, awbs = [], status = "" }) {
  // Feb 2026 (user cerinta): pentru cererile de garanție, dacă a fost deja
  // emis un AWB de ridicare ȘI coletul a ajuns în depozit (status ≥ verified),
  // recomandarea implicită se schimbă la "delivery" (AWB Livrare înapoi la
  // client). Altfel prima acțiune la garanție rămâne ridicarea.
  const hasPickup = awbs.some((a) => (a.direction || a.type || "").toLowerCase().includes("pickup") || (a.awb_type || "").toLowerCase().includes("pickup"));
  const parcelAtWarehouse = ["verified", "at_warehouse", "at_service", "refund_redeliver"].includes((status || "").toLowerCase());

  const recommendation = type === "schimb"
    ? "exchange"
    : type === "livrare"
      ? "delivery"
      : type === "retur"
        ? "pickup"
        : (hasPickup && parcelAtWarehouse ? "delivery" : "pickup"); // garantie: pickup first, apoi delivery cand coletul ajunge in depozit
  // Show ALL 3 options regardless of type so the user can always pick whichever
  // Sameday AWB they need (the type field is informational, not restrictive).
  return (
    <div className="relative">
      <button data-testid="action-add-awb" onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500/20 px-3 py-1.5 text-xs font-semibold text-emerald-300 ring-1 ring-emerald-500/40 hover:bg-emerald-500/30">
        <Truck className="h-3.5 w-3.5" /> Adaugă AWB <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <>
          <button className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-20 mt-1 w-72 overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950 shadow-2xl">
            <button data-testid="action-add-awb-exchange" onClick={() => onPick("exchange")}
              className={`flex w-full items-center gap-2 border-b border-zinc-800 px-3 py-2 text-left text-xs text-zinc-200 hover:bg-zinc-900 ${recommendation === "exchange" ? "bg-emerald-500/5" : ""}`}>
              <Truck className="h-3.5 w-3.5 text-emerald-300" />
              <div className="flex-1">
                <div className="font-medium">Schimb (2 AWB-uri) {recommendation === "exchange" && <span className="ml-1 rounded bg-emerald-500/20 px-1 text-[9px] text-emerald-300">recomandat</span>}</div>
                <div className="text-[10px] text-zinc-500">Emite automat 2 AWB-uri Sameday: <b>AWB Livrare</b> (colet nou → client) + <b>AWB Ridicare</b> (colet vechi ← client). Fiecare AWB e urmărit separat, statusul cererii avansează în 4 pași.</div>
              </div>
            </button>
            <button data-testid="action-add-awb-pickup" onClick={() => onPick("pickup")}
              className={`flex w-full items-center gap-2 border-b border-zinc-800 px-3 py-2 text-left text-xs text-zinc-200 hover:bg-zinc-900 ${recommendation === "pickup" ? "bg-orange-500/5" : ""}`}>
              <Truck className="h-3.5 w-3.5 text-orange-300" />
              <div className="flex-1">
                <div className="font-medium">AWB Client (ridicare) {recommendation === "pickup" && <span className="ml-1 rounded bg-orange-500/20 px-1 text-[9px] text-orange-300">recomandat</span>}</div>
                <div className="text-[10px] text-zinc-500">Ridică coletul de la client (ridicare terț)</div>
              </div>
            </button>
            <button data-testid="action-add-awb-delivery" onClick={() => onPick("delivery")}
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-zinc-200 hover:bg-zinc-900 ${recommendation === "delivery" ? "bg-sky-500/5" : ""}`}>
              <Truck className="h-3.5 w-3.5 text-sky-300" />
              <div className="flex-1">
                <div className="font-medium">AWB Livrare (către client) {recommendation === "delivery" && <span className="ml-1 rounded bg-sky-500/20 px-1 text-[9px] text-sky-300">recomandat</span>}</div>
                <div className="text-[10px] text-zinc-500">
                  {type === "garantie"
                    ? "Deschide PV Service → generează AWB + email cu PV atașat"
                    : type === "livrare"
                      ? "Livrare simplă cu opțiune ramburs (îți cere suma COD după click)"
                      : "Trimite coletul înapoi la client"}
                </div>
              </div>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// Open / download AWB PDFs through axios (so JWT auth header is sent) and use
// blob URLs so the browser opens or downloads them locally.
async function fetchAwbPdf(returnId, awbNumber, format, disposition) {
  const r = await axios.get(`${API}/returns/${returnId}/awbs/${awbNumber}/download`, {
    params: { format, disposition },
    responseType: "blob",
  });
  return new Blob([r.data], { type: "application/pdf" });
}

/**
 * Modal shown right after the PV + AWB combined flow completes. Mirrors the
 * "Open / Download" choice popup we already use on Orders so the operator can
 * either print the label immediately or grab a copy. Doesn't block the rest
 * of the page — clicking the backdrop dismisses it.
 */
function AwbReadyPopup({ returnId, awbNumber, label, onClose }) {
  const [busy, setBusy] = useState(false);
  const handleOpen = async () => {
    setBusy(true);
    try { await openAwbInTab(returnId, awbNumber); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  const handleDownload = async () => {
    setBusy(true);
    try { await downloadAwbPdf(returnId, awbNumber); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="awb-ready-popup">
      <div className="w-full max-w-md rounded-2xl border border-emerald-500/30 bg-zinc-950 p-5 shadow-2xl">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-400" />
            <h3 className="text-sm font-semibold text-zinc-100">{label || "AWB"} emis cu succes</h3>
          </div>
          <button data-testid="awb-popup-close" onClick={onClose} className="rounded-md p-1 text-zinc-500 hover:bg-zinc-900 hover:text-zinc-300">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mb-3 text-[11px] text-zinc-500">
          PV Service generat, email trimis către client cu PV atașat și status actualizat la
          <span className="ml-1 text-zinc-300">„În livrare către client&rdquo;.</span>
        </p>
        <div className="mb-4 rounded-lg border border-zinc-800 bg-zinc-900/60 px-3 py-2">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">AWB</span>
          <div className="font-mono text-sm font-semibold text-zinc-100" data-testid="awb-popup-number">{awbNumber || "—"}</div>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            data-testid="awb-popup-open"
            disabled={busy || !awbNumber}
            onClick={handleOpen}
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-sky-500 px-3 py-2 text-xs font-semibold text-zinc-950 hover:bg-sky-400 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
            Deschide AWB
          </button>
          <button
            data-testid="awb-popup-download"
            disabled={busy || !awbNumber}
            onClick={handleDownload}
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-xs font-semibold text-zinc-200 hover:border-emerald-500/40 hover:text-emerald-200 disabled:opacity-50"
          >
            <Download className="h-3.5 w-3.5" />
            Descarcă PDF
          </button>
        </div>
      </div>
    </div>
  );
}

async function openAwbInTab(returnId, awbNumber) {
  const blob = await fetchAwbPdf(returnId, awbNumber, "a6", "inline");
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener,noreferrer");
  // Revoke after a delay so the new tab has time to load
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

async function downloadAwbPdf(returnId, awbNumber) {
  const blob = await fetchAwbPdf(returnId, awbNumber, "a6", "attachment");
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `AWB_${awbNumber}_A6.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5_000);
}

import { useCounties, useCities } from "../hooks/useGeo";

// ---------- Address Editor with County/City dropdowns ----------
function AddressEditor({ address, city, county, postalCode, onSave, onPersist }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const display = [address, city, county].filter(Boolean).join(", ") || "—";
  const handleSaveModal = async (patch) => {
    // Update draft locally
    onSave(patch);
    // Persist immediately to DB so the AWB endpoint sees the new address right away
    if (onPersist) {
      setSaving(true);
      try {
        await onPersist({
          customer_address: patch.address,
          customer_city: patch.city,
          customer_county: patch.county,
          customer_postal_code: patch.postal_code,
          pickup_address: patch.address,
          pickup_city: patch.city,
          pickup_county: patch.county,
          pickup_postal_code: patch.postal_code,
        });
      } finally {
        setSaving(false);
      }
    }
    setOpen(false);
  };
  return (
    <>
      <KV label="Adresă livrare/ridicare">
        <button data-testid="address-trigger" onClick={() => setOpen(true)}
          className="group inline-flex w-full items-center gap-1 rounded px-1 py-0.5 text-left hover:bg-zinc-900">
          <span className={address ? "text-zinc-100" : "text-amber-400/80"}>{display}</span>
          {saving && <Loader2 className="ml-1 h-3 w-3 animate-spin text-zinc-500" />}
          <Edit2 className="ml-auto h-3 w-3 shrink-0 text-zinc-700 opacity-0 transition group-hover:opacity-100" />
        </button>
        {!address && (
          <span className="mt-0.5 inline-block text-[10px] text-amber-400/70">⚠ Adresa lipsește — necesară pentru AWB</span>
        )}
      </KV>
      {open && (
        <AddressEditModal
          initial={{ address: address || "", city: city || "", county: county || "", postal_code: postalCode || "" }}
          onClose={() => setOpen(false)}
          onSave={handleSaveModal}
        />
      )}
    </>
  );
}

function AddressEditModal({ initial, onClose, onSave }) {
  const [addr, setAddr] = useState(initial.address);
  const [countyName, setCountyName] = useState(initial.county);
  const [countyId, setCountyId] = useState(null);
  const [cityName, setCityName] = useState(initial.city);
  const [postal, setPostal] = useState(initial.postal_code);
  const { counties, loading: loadingCounties, error: cErr } = useCounties();
  const { cities, loading: loadingCities, error: ciErr } = useCities(countyId);
  const error = cErr || ciErr;

  // Match initial county name once counties load
  useEffect(() => {
    if (!counties.length || countyId) return undefined;
    const t = setTimeout(() => {
      const match = counties.find((c) => (c.name || "").toLowerCase() === (initial.county || "").toLowerCase());
      if (match) setCountyId(match.id);
    }, 0);
    return () => clearTimeout(t);
  }, [counties, countyId, initial.county]);

  const pickCounty = (e) => {
    const id = e.target.value;
    const c = counties.find((x) => String(x.id) === String(id));
    setCountyId(id ? Number(id) : null);
    setCountyName(c?.name || "");
    setCityName("");
  };
  const pickCity = (e) => {
    const name = e.target.value;
    setCityName(name);
    const c = cities.find((x) => x.name === name);
    if (c?.postal_code) setPostal(c.postal_code);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-md overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950 shadow-2xl">
        <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3">
          <h3 className="text-sm font-semibold text-zinc-100">Editează adresă livrare/ridicare</h3>
          <button data-testid="addr-close" onClick={onClose} className="rounded p-1 text-zinc-400 hover:text-zinc-100">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3 px-4 py-4">
          {error && (
            <div className="rounded border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200">{error}</div>
          )}
          <div>
            <label className="block text-[10px] font-medium uppercase tracking-wider text-zinc-500">Adresă (stradă, număr)</label>
            <textarea data-testid="addr-street" value={addr} onChange={(e) => setAddr(e.target.value)} rows={2}
              placeholder="Ex: Str. Florilor nr. 12, ap. 5"
              className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-100 outline-none focus:border-orange-500/40" />
          </div>
          <div>
            <label className="block text-[10px] font-medium uppercase tracking-wider text-zinc-500">Județ</label>
            <select data-testid="addr-county" value={countyId || ""} onChange={pickCounty}
              disabled={loadingCounties}
              className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-100 outline-none focus:border-orange-500/40 disabled:opacity-50">
              <option value="">{loadingCounties ? "Se încarcă..." : "— Alege județ —"}</option>
              {counties.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-medium uppercase tracking-wider text-zinc-500">Oraș / localitate</label>
            <select data-testid="addr-city" value={cityName} onChange={pickCity}
              disabled={!countyId || loadingCities}
              className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-100 outline-none focus:border-orange-500/40 disabled:opacity-50">
              <option value="">{!countyId ? "Alege întâi județul" : loadingCities ? "Se încarcă..." : "— Alege oraș —"}</option>
              {cities.map((c) => (
                <option key={c.id} value={c.name}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-medium uppercase tracking-wider text-zinc-500">Cod poștal (opțional)</label>
            <input data-testid="addr-postal" value={postal} onChange={(e) => setPostal(e.target.value)}
              className="mt-1 w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-100 outline-none focus:border-orange-500/40" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-zinc-800 bg-zinc-950 px-4 py-3">
          <button onClick={onClose} className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:text-zinc-100">
            Renunță
          </button>
          <button data-testid="addr-save" onClick={() => onSave({ address: addr, county: countyName, city: cityName, postal_code: postal })}
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-zinc-950 hover:bg-emerald-400">
            <CheckCircle2 className="h-3.5 w-3.5" /> Salvează în formular
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- AWBs card ----------
function AwbsCard({ awbs, onRefresh, onCancel, onRemoveOnly, type, returnId }) {
  const [busyAwb, setBusyAwb] = useState(null);
  const handleOpen = async (number) => {
    setBusyAwb(`open-${number}`);
    try { await openAwbInTab(returnId, number); }
    catch (e) { window.alert(`Eroare deschidere AWB: ${e.response?.data?.detail || e.message}`); }
    finally { setBusyAwb(null); }
  };
  const handleDownload = async (number) => {
    setBusyAwb(`dl-${number}`);
    try { await downloadAwbPdf(returnId, number); }
    catch (e) { window.alert(`Eroare descărcare: ${e.response?.data?.detail || e.message}`); }
    finally { setBusyAwb(null); }
  };
  return (
    <Card title={`AWB(uri) (${awbs.length})`} icon={Truck}>
      {awbs.length === 0 && (
        <p className="text-xs text-zinc-500">Niciun AWB generat. Apasă „Adaugă AWB” pentru a începe.</p>
      )}
      <div className="space-y-3">
        {awbs.map((a) => {
          const isPickup = a.kind === "pickup";
          // Legacy: "exchange" (combined single AWB) — no longer created but
          // may exist on old requests. Treated as a delivery for labeling.
          const isLegacyExchange = !!a.exchange;
          const badgeCls = isLegacyExchange
            ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
            : isPickup
              ? "bg-orange-500/15 text-orange-300 border-orange-500/40"
              : "bg-sky-500/15 text-sky-300 border-sky-500/40";
          const kindLabel = isLegacyExchange
            ? "Colet la schimb (legacy)"
            : isPickup ? "AWB Ridicare (colet vechi ← client)" : "AWB Livrare (colet nou → client)";
          return (
            <div key={a.id || a.number} data-testid={`awb-row-${a.number}`} className="rounded-xl border border-zinc-800 bg-zinc-950/80 p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded bg-rose-500/10 px-2 py-0.5 font-mono text-[10px] uppercase text-rose-300">SAMEDAY</span>
                <span className="font-mono text-sm font-semibold text-emerald-300">{a.number}</span>
                <span className={`inline-flex items-center rounded border px-2 py-0.5 text-[10px] font-semibold uppercase ${badgeCls}`}>
                  {kindLabel}
                </span>
                <span className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/5 px-2 py-0.5 text-[10px] text-emerald-300">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
                  Auto-refresh activ
                </span>
              </div>

              {/* Status curent */}
              <div className="mt-2.5 rounded-lg border border-zinc-800 bg-zinc-900/40 px-3 py-2">
                <p className="text-[10px] font-medium uppercase tracking-wider text-zinc-500">Status curent</p>
                <p className="mt-0.5 text-sm font-medium text-zinc-100">{a.status_text || a.status || "—"}</p>
                {a.last_refresh_at && (
                  <p className="mt-0.5 text-[10px] text-zinc-500">Sincronizat: {fmtDateTime(a.last_refresh_at)}</p>
                )}
              </div>

              <div data-testid={`awb-invoiced-cost-${a.number}`} className="mt-2 text-[11px] text-zinc-500">
                Cost Sameday (desfășurător, fără TVA):{" "}
                {a.invoiced_cost != null
                  ? <span className="font-semibold text-zinc-200">{Number(a.invoiced_cost).toFixed(2)} RON</span>
                  : <span className="text-amber-400">Nefacturat încă — nu e în desfășurător</span>}
              </div>
              {a.cash_on_delivery > 0 && (
                <div data-testid={`awb-cod-${a.number}`} className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[11px] font-semibold text-amber-300">
                  <Wallet className="h-3 w-3" /> Ramburs: {Number(a.cash_on_delivery).toFixed(2)} RON
                </div>
              )}
              {isPickup && (a.contact_name || a.contact_phone) && (
                <div className="mt-1 text-[11px] text-zinc-500">
                  Contact destinatar: <span className="text-zinc-300">{a.contact_name}</span>
                  {a.contact_phone && <> · <span className="text-zinc-300">{a.contact_phone}</span></>}
                  {a.contact_email && <> · <span className="text-zinc-300">{a.contact_email}</span></>}
                </div>
              )}
              {a.dimensions && (
                <div className="mt-1 text-[10px] text-zinc-600">
                  Dimensiuni: {a.dimensions.weight_kg ?? "?"} kg ·
                  {" "}{a.dimensions.length_cm ?? "?"}×{a.dimensions.width_cm ?? "?"}×{a.dimensions.height_cm ?? "?"} cm
                  {a.dimensions.parcels > 1 && <> · {a.dimensions.parcels} colete</>}
                </div>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <button data-testid={`awb-open-${a.number}`} onClick={() => handleOpen(a.number)} disabled={busyAwb === `open-${a.number}`}
                  className="inline-flex items-center gap-1 rounded border border-zinc-800 bg-zinc-900 px-2 py-1 text-[10px] text-zinc-200 hover:border-sky-500/40 hover:text-sky-300 disabled:opacity-50">
                  {busyAwb === `open-${a.number}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />} Deschide A6
                </button>
                <button data-testid={`awb-a6-${a.number}`} onClick={() => handleDownload(a.number)} disabled={busyAwb === `dl-${a.number}`}
                  className="inline-flex items-center gap-1 rounded border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 text-[10px] text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-50">
                  {busyAwb === `dl-${a.number}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />} Descarcă A6
                </button>
                <button data-testid={`awb-refresh-${a.number}`} onClick={() => onRefresh(a.number)}
                  className="inline-flex items-center gap-1 rounded border border-zinc-800 bg-zinc-900 px-2 py-1 text-[10px] text-zinc-200 hover:border-sky-500/40 hover:text-sky-300">
                  <RefreshCw className="h-3 w-3" /> Refresh acum
                </button>
                <button data-testid={`awb-cancel-${a.number}`} onClick={() => onCancel(a.number)}
                  className="inline-flex items-center gap-1 rounded border border-rose-500/40 bg-rose-500/10 px-2 py-1 text-[10px] text-rose-300 hover:bg-rose-500/20">
                  <Trash2 className="h-3 w-3" /> Anulează în Sameday
                </button>
                <button data-testid={`awb-remove-${a.number}`} onClick={() => onRemoveOnly(a.number)}
                  title="Elimină AWB-ul doar din această cerere, fără a-l anula în Sameday"
                  className="inline-flex items-center gap-1 rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-[10px] text-zinc-300 hover:border-amber-500/40 hover:text-amber-300">
                  <X className="h-3 w-3" /> Șterge din cerere
                </button>
                <span className="ml-auto text-[10px] text-zinc-500">Ultimul eveniment: {fmtDateTime(a.last_event_at)}</span>
              </div>
              {a.events?.length > 0 && (
                <div className="mt-3 space-y-1.5 border-t border-zinc-900 pt-2">
                  {a.events.slice(0, 6).map((e, i) => (
                    <div key={i} className="flex items-start gap-2 text-[11px]">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400" />
                      <div>
                        <p className="text-zinc-300">{e.text}</p>
                        <p className="text-[10px] text-zinc-500">{fmtDateTime(e.at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ---------- Products card ----------
function ProductsCard({ products }) {
  return (
    <Card title={`Produse (${products.length})`} icon={Package}>
      {products.length === 0 && <p className="text-xs text-zinc-500">Niciun produs.</p>}
      <div className="space-y-2">
        {products.map((p, i) => (
          <div key={i} className="flex items-start gap-3 rounded-xl border border-zinc-800 bg-zinc-900/40 p-2.5">
            {p.image ? (
              <img src={p.image} alt="" className="h-12 w-12 shrink-0 rounded-md border border-zinc-800 object-cover" />
            ) : (
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-zinc-800 bg-zinc-900 text-zinc-700">
                <Package className="h-5 w-5" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-xs text-zinc-100">{p.name || "—"}</p>
              <p className="mt-0.5 text-[10px] text-zinc-500">{p.part_number || "—"} · x{p.quantity || 1}</p>
            </div>
            <div className="text-right text-xs">
              <p className="font-semibold text-zinc-100">{p.sale_price != null ? `${Number(p.sale_price).toFixed(2)} ${p.currency || "RON"}` : "—"}</p>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ---------- Attachments card ----------
function AttachmentsCard({ returnId, attachments, reload, setError }) {
  const [uploading, setUploading] = useState(false);
  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/returns/${returnId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await reload();
    } catch (er) { setError(er.response?.data?.detail || er.message); }
    finally { setUploading(false); }
  };
  const onDelete = async (att) => {
    if (!window.confirm(`Șterge ${att.original_name || att.filename}?`)) return;
    try {
      await axios.delete(`${API}/returns/${returnId}/attachments/${att.id}`);
      await reload();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };
  return (
    <Card title={`Atașamente (${attachments.length})`} icon={Paperclip}>
      <div className="mb-3 flex items-center gap-2">
        <label data-testid="attach-upload" className={`inline-flex cursor-pointer items-center gap-1 rounded border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-[11px] ${uploading ? "opacity-50" : "hover:border-orange-500/40 hover:text-orange-300 text-zinc-200"}`}>
          {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} Încarcă PDF / imagine
          <input type="file" className="hidden" accept="application/pdf,image/*" onChange={onUpload} disabled={uploading} />
        </label>
      </div>
      {attachments.length === 0 && <p className="text-xs text-zinc-500">Niciun atașament.</p>}
      <div className="space-y-1.5">
        {attachments.map((a) => (
          <div key={a.id} data-testid={`attach-${a.id}`} className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900/40 px-2.5 py-1.5 text-[11px]">
            <Paperclip className="h-3 w-3 text-zinc-500" />
            <span className="flex-1 truncate text-zinc-100">{a.original_name || a.filename}</span>
            <span className="text-[10px] text-zinc-500">{(a.size / 1024).toFixed(1)} KB</span>
            <a href={`${BACKEND_URL}${a.url}`} target="_blank" rel="noreferrer" className="rounded p-0.5 text-zinc-400 hover:text-emerald-300"><Download className="h-3 w-3" /></a>
            <button data-testid={`attach-del-${a.id}`} onClick={() => onDelete(a)} className="rounded p-0.5 text-zinc-400 hover:text-rose-300"><Trash2 className="h-3 w-3" /></button>
          </div>
        ))}
      </div>
    </Card>
  );
}
